/**
 * Process folder purpose presets.
 *
 * A folder's `processPurpose` answers "what kind of process work is this
 * folder for?" — a sub-axis within domain='process'. New diagrams in the
 * folder inherit the preset's defaults (which field packs are on, which
 * compliance frameworks are pre-selected, which AI prompts the assistant
 * leads with). Diagrams can override per-diagram.
 *
 * The chooser is shown as the empty-state on a process folder with no
 * purpose set; once chosen, a folder header chip exposes change/clear.
 *
 * Keep this module pure data — no React, no DB. Imported by the chooser,
 * the diagram-default seeder, and the folder PATCH validator.
 */

export const PROCESS_PURPOSE_IDS = [
  'quick_flowchart',
  'documented_process',
  'audit_ready_compliance',
  'grc_compass',
] as const;

export type ProcessPurposeId = (typeof PROCESS_PURPOSE_IDS)[number];

export const isProcessPurposeId = (value: unknown): value is ProcessPurposeId =>
  typeof value === 'string' && (PROCESS_PURPOSE_IDS as readonly string[]).includes(value);

export interface ProcessPurposePreset {
  id: ProcessPurposeId;
  /** Short label for chips, headers, card titles. */
  label: string;
  /** One-line subtitle for cards. */
  tagline: string;
  /** Persona hint — surfaced in the chooser card footer. */
  personaHint: string;
  /** lucide-react icon name (looked up by the chooser). */
  iconName: 'sparkles' | 'clipboard-list' | 'shield-check' | 'network';
  /** Optional process-* field pack IDs to enable on new diagrams. */
  defaultFieldPacks: ReadonlyArray<string>;
  /** Compliance framework IDs to pre-select on new diagrams. */
  defaultFrameworks: ReadonlyArray<string>;
  /** Optional process taxonomy prior pack selected for this folder purpose. */
  taxonomyPackId?: 'apqc-pcf';
  /** Control point category IDs to pre-enable on new diagrams. */
  defaultControlCategories: ReadonlyArray<string>;
  /** Whether to surface the Compass / knowledge-source CTA on the folder. */
  showCompassCta: boolean;
  /** Whether to recommend (nudge) connecting a knowledge source. */
  recommendSource: boolean;
  /** Suggested AI prompt set — consumed by ProcessMappingAssistantPanel. */
  aiPrompts: ReadonlyArray<string>;
}

export const PROCESS_PURPOSE_PRESETS: Record<ProcessPurposeId, ProcessPurposePreset> = {
  quick_flowchart: {
    id: 'quick_flowchart',
    label: 'Quick flowchart',
    tagline: 'Sketch a process in minutes with AI + simple shapes.',
    personaHint: 'Best for one-off diagrams, SOPs, onboarding docs.',
    iconName: 'sparkles',
    defaultFieldPacks: [],
    defaultFrameworks: [],
    defaultControlCategories: [],
    showCompassCta: false,
    recommendSource: false,
    aiPrompts: [
      'Describe a process →',
      'Generate steps from notes',
      'Suggest decision branches',
    ],
  },
  documented_process: {
    id: 'documented_process',
    label: 'Documented process library',
    tagline: 'Track owners, durations, SLAs, and tooling across views.',
    personaHint: 'Best for process analysts, ops teams, SOP libraries.',
    iconName: 'clipboard-list',
    defaultFieldPacks: ['process-tools', 'process-automation', 'process-cost'],
    defaultFrameworks: [],
    taxonomyPackId: 'apqc-pcf',
    defaultControlCategories: [],
    showCompassCta: false,
    recommendSource: true,
    aiPrompts: [
      'Group steps into swim lanes',
      'Assign owners and durations',
      'Find bottlenecks and handoffs',
      'Identify automation candidates',
    ],
  },
  audit_ready_compliance: {
    id: 'audit_ready_compliance',
    label: 'Audit-ready compliance',
    tagline: 'Map controls, attach evidence, score gaps against frameworks.',
    personaHint: 'Best for compliance officers, internal audit, SOC 2 / ISO prep.',
    iconName: 'shield-check',
    defaultFieldPacks: ['process-compliance', 'process-risk'],
    defaultFrameworks: ['soc2'],
    taxonomyPackId: 'apqc-pcf',
    defaultControlCategories: [
      'access-control',
      'change-management',
      'data-protection',
      'monitoring-logging',
    ],
    showCompassCta: true,
    recommendSource: true,
    aiPrompts: [
      'Map controls to steps',
      'Identify control gaps',
      'Suggest evidence per step',
      'Score against SOC 2 controls',
    ],
  },
  grc_compass: {
    id: 'grc_compass',
    label: 'GRC + Compass',
    tagline: 'Connect policy ↔ infrastructure ↔ process ↔ people in one graph.',
    personaHint: 'Best for GRC managers, multi-framework programs, audit bundles.',
    iconName: 'network',
    defaultFieldPacks: ['process-compliance', 'process-risk', 'process-tools'],
    defaultFrameworks: ['soc2', 'iso27001'],
    taxonomyPackId: 'apqc-pcf',
    defaultControlCategories: [
      'access-control',
      'change-management',
      'data-protection',
      'monitoring-logging',
      'vendor-management',
      'incident-response',
    ],
    showCompassCta: true,
    recommendSource: true,
    aiPrompts: [
      'Connect a policy or infrastructure source',
      'Find framework gaps across frameworks',
      'Map controls to systems and people',
      'Draft remediations for unmet controls',
    ],
  },
};

export const PROCESS_PURPOSE_LIST: ReadonlyArray<ProcessPurposePreset> = PROCESS_PURPOSE_IDS.map(
  (id) => PROCESS_PURPOSE_PRESETS[id],
);

export const getProcessPurposePreset = (
  id: ProcessPurposeId | null | undefined,
): ProcessPurposePreset | null => (id && isProcessPurposeId(id) ? PROCESS_PURPOSE_PRESETS[id] : null);

/**
 * Subset of `DiagramSettings.process` that this preset seeds on a fresh
 * diagram. Kept loose-typed (string arrays) so the preset module stays
 * free of editor-side types.
 */
export interface ProcessPresetSeed {
  enabledFieldPacks: string[];
  complianceFrameworks: string[];
  controlPointCategories: string[];
}

/**
 * Build the seed object for a new diagram in a folder with the given
 * purpose. Returns null when the purpose is unset or unknown — callers
 * should leave settings untouched in that case.
 *
 * Wire this into the new-diagram creation path so a folder labelled
 * "audit-ready compliance" produces diagrams that already have SOC 2
 * + Compliance pack enabled instead of starting blank.
 */
export const buildProcessPresetSeed = (
  id: ProcessPurposeId | null | undefined,
): ProcessPresetSeed | null => {
  const preset = getProcessPurposePreset(id);
  if (!preset) return null;
  return {
    enabledFieldPacks: [...preset.defaultFieldPacks],
    complianceFrameworks: [...preset.defaultFrameworks],
    controlPointCategories: [...preset.defaultControlCategories],
  };
};
